/**
 * Mirror of the `fim-photo` Rolodex `data` contract. The entry is the ONLY
 * persisted artifact of an attachment; fusion re-derives the subject linkage
 * from `elections` on every run.
 */

import { t } from '@lingui/core/macro'
import type { FimEntry, SubjectKind } from 'features/fim/types'
import { z } from 'zod'

export const FIM_ENTRY_PHOTO = 'fim-photo'
export const PHOTO_FILE_PATH = 'photo.jpg'
export const PHOTO_THUMBNAIL_PATH = 'thumbnail_photo.jpg'

export type PhotoStatus = 'pending_upload' | 'unattached' | 'attached'

export type PhotoSource = 'subject_page' | 'subject_panel' | 'chat' | 'doc_chat'

export interface PhotoUploadedBy {
  email: string
  name: string | null
  user_id: string | null
}

export type CapturedAtSource = 'exif' | 'upload'

/** The uploading browser; `install_id` is the closest thing to a device id. */
export interface PhotoClient {
  install_id: string
  user_agent: string
  platform: string
  app_version: string | null
}

export interface PhotoGps {
  lat: number
  lon: number
}

/**
 * Where the device said it was when the photo was taken. Phones strip EXIF
 * location from files they hand to a web page, so this is asked for directly.
 */
export interface PhotoLocation {
  lat: number
  lon: number
  accuracy_m: number | null
  fixed_at: string
}

/** The few EXIF fields read from the ORIGINAL file before re-encode. */
export interface PhotoExif {
  make: string | null
  model: string | null
  orientation: number | null
  gps: PhotoGps | null
}

/** Provenance written once at entry creation. */
export interface PhotoCaptureMetadata {
  uploaded_by: PhotoUploadedBy | null
  uploaded_at: string
  captured_at: string
  captured_at_source: CapturedAtSource
  client: PhotoClient
  exif: PhotoExif | null
}

/** Cached VLM output; written once by the analyze step, read-only after. */
export interface PhotoAnalysis {
  equipment_kind?: string | null
  label?: string | null
  tag_texts?: string[]
  nameplate_fields?: Record<string, string>
  scene_description?: string | null
  confidence?: number | null
}

/** One subject this photo is pinned to: the fusion-facing state. */
export const photoElectionSchema = z
  .object({
    n: z.number().int().nonnegative(),
    subject_identity_norm: z.string().min(1),
    // Display hint only; keyless subjects can re-key.
    subject_uid_at_election: z.string().nullable().optional(),
    equipment_kind: z.string().nullable().optional(),
    elected_at: z.string().nullable().optional(),
    hero_at: z.string().nullable().optional(),
  })
  .passthrough()

export type PhotoElection = z.infer<typeof photoElectionSchema>

export interface FimPhotoData {
  site: string
  status: PhotoStatus
  primary_label: string | null
  caption: string | null
  captured_at: string
  source: PhotoSource
  chat_session_id: string | null
  mime_type: string
  analysis: PhotoAnalysis | null
  elections: PhotoElection[]
  /** Capture provenance; absent on entries created before it existed. */
  uploaded_by?: PhotoUploadedBy | null
  uploaded_at?: string
  captured_at_source?: CapturedAtSource
  client?: PhotoClient
  exif?: PhotoExif | null
  location?: PhotoLocation | null
  /** SHA-256 of the original file; the same bytes never mint a second entry. */
  content_sha256?: string | null
}

export type FimPhotoEntry = FimEntry<FimPhotoData>

/**
 * When an election will land on the model: at the next full fusion run, or,
 * for a subject with no must-link-bindable identity norm (e.g. keyless),
 * only once the subject gains a high-precision identity alias.
 */
export type PhotoNaturalization =
  | 'next_fusion_run'
  | 'after_subject_gains_identity'

/**
 * One photo as rendered on a subject. `pending` means the election exists on
 * the photo entry but fusion has not yet landed the reference on the subject
 * payload (naturalization happens at the next full run).
 */
export interface SubjectPhoto {
  entry: FimPhotoEntry
  pending: boolean
  thumbnailUrl: string | undefined
  imageUrl: string | undefined
}

const ELECTION_KINDS: ReadonlySet<SubjectKind> = new Set<SubjectKind>([
  'equipment',
  'instrument',
  'system',
  'subsystem',
  'pipeline',
])

/** The election `equipment_kind` for a subject; non-asset kinds pin as equipment. */
export const electionKind = (subjectKind: SubjectKind): SubjectKind =>
  ELECTION_KINDS.has(subjectKind) ? subjectKind : 'equipment'

export const photoDisplayTitle = (entry: FimPhotoEntry): string =>
  entry.data.primary_label ?? entry.data.caption ?? t`Photo`
